<script setup lang="ts">
import ProgressFrame from './layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { computed, onMounted, watch } from 'vue';
import ProgressBar from '../components/ProgressBar.vue';
import {
  COMMISSION_STATUS,
  COMMISSION_CONFIG_STATUS,
  COMMISSION_RESULT_STATUS,
} from '../utils/constants';
import { useRouter } from 'vue-router';

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;
const isUploadConfig = commissioningDeviceStore.isUploadConfig;
const router = useRouter();

onMounted(async () => {
  // Check if the serial number is set; if not, navigate to the enter serial number page
  if (!serialNumber) {
    router.push({ name: 'SerialNumberHow' });
    return;
  }

  try {
    if (isUploadConfig) {
      // Start listening to the config update status
      await commissioningDeviceStore.pollUpdateConfigStatus(serialNumber);
    }
  } catch (error) {
    // A potential timeout error occurred, navigate to the commission failed page
    commissioningDeviceStore.setUploadConfigState(COMMISSION_CONFIG_STATUS.FAILED);
    router.push({ name: 'CommissionFailed' });
  }

  try {
    if (!isUploadConfig) {
      // Start listening to the commission status
      await commissioningDeviceStore.pollCommissionState(serialNumber);
    }
  } catch (error) {
    // A potential timeout error occurred, navigate to the commission failed page
    commissioningDeviceStore.setCommissioningLogState(COMMISSION_STATUS.FAILED);
    router.push({ name: 'CommissionFailed' });
  }
});

// A computed property that keeps track if the states for the following are done:
// 1. Config update is complete / failed
// 2. Commission is complete / failed
// 3. Commission result is complete / failed
const configUpdateDone = computed(() => {
  return (
    commissioningDeviceStore.configUploadLog.status !== null &&
    [COMMISSION_CONFIG_STATUS.SUCCESS, COMMISSION_CONFIG_STATUS.FAILED].includes(
      commissioningDeviceStore.configUploadLog.status,
    )
  );
});

const commissionDone = computed(() => {
  return (
    commissioningDeviceStore.commissioningLog.status !== null &&
    [COMMISSION_STATUS.COMPLETE, COMMISSION_STATUS.FAILED].includes(
      commissioningDeviceStore.commissioningLog.status,
    )
  );
});

const commissionResultDone = computed(() => {
  return (
    commissioningDeviceStore.commissionResultStatus.status !== null &&
    [COMMISSION_RESULT_STATUS.SUCCESS, COMMISSION_RESULT_STATUS.FAILED].includes(
      commissioningDeviceStore.commissionResultStatus.status,
    )
  );
});

// Watch if the update config is done, if so, start the commission
// and listen to the commission status...
watch(configUpdateDone, async (isDone) => {
  if (!isDone) return;

  if (commissioningDeviceStore.configUploadLog.status === COMMISSION_CONFIG_STATUS.FAILED) {
    router.push({ name: 'CommissionFailed' });
    return;
  }

  try {
    await commissioningDeviceStore.startCommission(serialNumber, async () => {
      await commissioningDeviceStore.pollCommissionState(serialNumber);
    });
  } catch (error) {
    commissioningDeviceStore.setCommissioningLogState(COMMISSION_STATUS.FAILED);
    router.push({ name: 'CommissionFailed' });
  }
});

// Watch if the commission is done, if so, trigger the commission result
// and listen to the commission result status...
watch(commissionDone, async (isDone) => {
  if (!isDone) return;

  if (commissioningDeviceStore.commissioningLog.status === COMMISSION_STATUS.FAILED) {
    router.push({ name: 'CommissionFailed' });
    return;
  }

  try {
    await commissioningDeviceStore.fetchCommissionResultWithPolling(
      serialNumber,
      commissioningDeviceStore.commissioningId ?? '',
    );
  } catch (error) {
    commissioningDeviceStore.setCommissionResultStatusState(COMMISSION_RESULT_STATUS.FAILED);
    router.push({ name: 'CommissionFailed' });
  }
});

// Watch if the commission result is done, if successful, call the command to complete the commission
// Otherwise, navigate to the commission failed page.
watch(commissionResultDone, async (isDone) => {
  if (!isDone) return;

  if (commissioningDeviceStore.commissionResultStatus.status === COMMISSION_RESULT_STATUS.FAILED) {
    router.push({ name: 'CommissionFailed' });
    return;
  }

  try {
    await commissioningDeviceStore.completeCommission(serialNumber, async () => {
      router.push({ name: 'CommissionSucceeded' });
    });
  } catch (error) {
    commissioningDeviceStore.setCommissionResultStatusState(COMMISSION_RESULT_STATUS.FAILED);
    router.push({ name: 'CommissionFailed' });
  }
});

const progressBarStates = computed(() => [
  {
    title: 'Configuration Update',
    doneRemarks: isUploadConfig ? '' : 'No changes made',
    progress: commissioningDeviceStore.configUploadLog.startPercent ?? 0,
    progressText: commissioningDeviceStore.configUploadLog.text,
    success: commissioningDeviceStore.configUploadLog.status === COMMISSION_CONFIG_STATUS.SUCCESS,
    failure: commissioningDeviceStore.configUploadLog.status === COMMISSION_CONFIG_STATUS.FAILED,
  },
  {
    title: 'Commissioning Test',
    doneRemarks: '',
    progress:
      0.9 * (commissioningDeviceStore.commissioningLog.startPercent ?? 0) +
      0.1 * (commissioningDeviceStore.commissionResultStatus.startPercent ?? 0),
    progressText: !commissionDone.value
      ? commissioningDeviceStore.commissioningLog.text
      : !commissionResultDone.value
      ? commissioningDeviceStore.commissionResultStatus.text
      : '',
    success:
      commissioningDeviceStore.commissioningLog.status === COMMISSION_STATUS.COMPLETE &&
      commissioningDeviceStore.commissionResultStatus.status === COMMISSION_RESULT_STATUS.SUCCESS,
    failure:
      commissioningDeviceStore.commissioningLog.status === COMMISSION_STATUS.FAILED ||
      commissioningDeviceStore.commissionResultStatus.status === COMMISSION_RESULT_STATUS.FAILED,
  },
]);
</script>

<template>
  <ProgressFrame pageClass="subsection-page" :titleTight="true" subTitleColor="secondary">
    <template #title>
      <div class="title-container">Commissioning in Progress</div>
    </template>
    <template #subTitle> {{ serialNumber }} </template>
    <div class="content">
      <p class="instruction-text">Please wait while we're running the test.</p>
      <div class="test-progress-container">
        <div v-for="test in progressBarStates" :key="test.title">
          <ProgressBar
            :title="test.title"
            :progress="test.progress"
            :progressText="test.progressText"
            :success="test.success"
            :failure="test.failure"
            :doneRemarks="test.doneRemarks"
          />
        </div>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.title-container {
  margin-top: 1rem;
  font-size: 2rem;
  font-weight: @font-weight-regular;
}
.content {
  text-align: center;
  .instruction-text {
    font-weight: @font-weight-medium;
  }

  .test-progress-container {
    width: 100%;
  }

  .test-progress-container > div {
    margin-bottom: 1rem;
  }
}
</style>
